<template>
    <div :class="statusClass">
        <h5 v-if="isClickableStatus" :class="titleClass" style="cursor: pointer" @click="handleStatusClick">
            {{ titleText }}
            <span class="dropdown-arrow" :class="{ open: showDropdown }">▼</span>
        </h5>
        <h5 v-else :class="titleClass">
            {{ titleText }}
        </h5>
        <div v-if="showDropdown" class="status-dropdown">
            <div v-if="isProcessing" style="padding: 10px 14px">
                <common-logo-spinner :width="36"></common-logo-spinner>
            </div>
            <h5
                v-for="option in dropdownOptions"
                v-else
                :key="option"
                :class="['status-dropdown__option', dropdownClass(option)]"
                @click="selectStatus(option)"
            >
                {{ $t(`pages.referrals.tracking.status_title.${option}`) }}
            </h5>
        </div>
        <p :class="descriptionClass">
            <slot v-if="showDescriptionText" name="description">
                {{ descriptionText }}
            </slot>
            <span v-if="descriptionTextStrikethrough" style="text-decoration: line-through">
                {{ descriptionTextStrikethrough }}
            </span>
        </p>
    </div>
</template>

<script>
    import ReferralStatusMixin from 'mixins/ReferralStatusMixin'
    import ReferralStatusEnum from '@/enums/ReferralStatusEnum'
    import ReferralsAPI from 'api/ReferralsAPI'

    export default {
        mixins: [ReferralStatusMixin],

        props: {
            referral: { type: Object, required: true },
            isInline: { type: Boolean, default: false },
            description: { type: String, default: null },
            descriptionStrike: { type: String, default: null },
            showDescriptionText: { type: Boolean, default: true },
            isStatusChangeable: { type: Boolean, default: false },
        },

        data() {
            return {
                showDropdown: false,
                isProcessing: false,
            }
        },

        computed: {
            isReferralsRouteMade() {
                return this.$route.params.type === 'made'
            },
            isClickableStatus() {
                if (this.isReferralsRouteMade) {
                    return this.isStatusChangeable && this.referral.status === ReferralStatusEnum.CREATED
                }
                return (
                    this.isStatusChangeable &&
                    (this.referral.status === ReferralStatusEnum.NO_SHOW ||
                        this.referral.status === ReferralStatusEnum.CONSULTATION_NOTES_PENDING ||
                        this.referral.status === ReferralStatusEnum.CANCELLED ||
                        this.referral.status === ReferralStatusEnum.PATIENT_VISIT_PENDING)
                )
            },
            dropdownOptions() {
                if (this.isReferralsRouteMade && this.referral.status === ReferralStatusEnum.CREATED) {
                    return [ReferralStatusEnum.CANCELLED]
                }
                if (this.referral.status === ReferralStatusEnum.NO_SHOW) {
                    return [
                        ReferralStatusEnum.CONSULTATION_NOTES_PENDING,
                        ReferralStatusEnum.CANCELLED,
                        ReferralStatusEnum.PATIENT_VISIT_PENDING,
                    ]
                }
                if (this.referral.status === ReferralStatusEnum.CONSULTATION_NOTES_PENDING) {
                    return [ReferralStatusEnum.NO_SHOW, ReferralStatusEnum.CANCELLED, ReferralStatusEnum.PATIENT_VISIT_PENDING]
                }
                if (this.referral.status === ReferralStatusEnum.CANCELLED) {
                    return [
                        ReferralStatusEnum.NO_SHOW,
                        ReferralStatusEnum.CONSULTATION_NOTES_PENDING,
                        ReferralStatusEnum.PATIENT_VISIT_PENDING,
                    ]
                }
                if (this.referral.status === ReferralStatusEnum.PATIENT_VISIT_PENDING) {
                    return [
                        ReferralStatusEnum.NO_SHOW,
                        ReferralStatusEnum.CANCELLED,
                        ReferralStatusEnum.CONSULTATION_NOTES_PENDING,
                    ]
                }
                return []
            },
            statusClass() {
                return {
                    'tracking-status': true,
                    'tracking-status--is-inline': this.isInline,
                }
            },

            isPending() {
                return this.isReferralPending(this.referral)
            },

            isPendingReschedule() {
                return this.isReferralPendingReschedule(this.referral)
            },

            isToBeRescheduled() {
                return this.isReferralToBeRescheduled(this.referral)
            },

            isOk() {
                return this.isReferralOk(this.referral)
            },

            isCompleted() {
                return this.isReferralCompleted(this.referral)
            },

            isError() {
                return this.isReferralError(this.referral)
            },

            titleClass() {
                return {
                    'tracking-status__title': true,
                    'tracking-status__title--color-orange': this.isPending || this.isToBeRescheduled || this.isPendingReschedule,
                    'tracking-status__title--color-blue': this.isCompleted,
                    'tracking-status__title--color-green': this.isOk,
                    'tracking-status__title--color-red': this.isError,
                }
            },

            titleText() {
                return this.$t(`pages.referrals.tracking.status_title.${this.referral.status}`)
            },

            descriptionClass() {
                return {
                    'tracking-status__description': true,
                    'tracking-status__description--color-yellow':
                        !!this.descriptionText &&
                        (this.referral.status === ReferralStatusEnum.RESCHEDULE_REQUESTED ||
                            this.referral.status === ReferralStatusEnum.RESCHEDULE_AVAILABILITY_CHANGE ||
                            this.referral.status === ReferralStatusEnum.CONSULTATION_NOTES_PENDING ||
                            this.referral.status === ReferralStatusEnum.INSURANCE_AUTH_PENDING),
                }
            },

            descriptionText() {
                if (this.$route.path === '/referrals/received') {
                    if (this.description) return this.description
                    return this.$t(`pages.referrals.tracking.status_description.${this.referral.status}`)
                }

                return ''
            },

            descriptionTextStrikethrough() {
                if (this.descriptionStrike) return this.descriptionStrike
                else return ''
            },
        },
        methods: {
            getApiActionForStatus(selectedStatus) {
                switch (selectedStatus) {
                    case ReferralStatusEnum.NO_SHOW:
                        return {
                            api: ReferralsAPI.updatePatientVisitStatus,
                            args: [this.referral.id, 'no-show'],
                        }

                    case ReferralStatusEnum.CONSULTATION_NOTES_PENDING:
                        return {
                            api: ReferralsAPI.updatePatientVisitStatus,
                            args: [this.referral.id, 'patient-seen'],
                        }

                    case ReferralStatusEnum.CANCELLED:
                        return {
                            api: ReferralsAPI.cancel,
                            args: [
                                this.$auth.isUserPracticeOther(),
                                this.referral.id,
                                this.$auth.getActiveMember() && this.$auth.getActiveMember().id
                                    ? this.$auth.getActiveMember().id
                                    : this.$auth.getUser().id,
                            ],
                        }

                    case ReferralStatusEnum.PATIENT_VISIT_PENDING:
                        return {
                            api: ReferralsAPI.accept,
                            args: [this.referral.id],
                        }

                    default:
                        return null
                }
            },
            handleStatusClick() {
                if (!this.isClickableStatus) return
                this.showDropdown = !this.showDropdown
            },
            selectStatus(option) {
                this.isProcessing = true
                this.handleStatusChange(option)
            },
            dropdownClass(option) {
                return {
                    'tracking-status__title': true,
                    'tracking-status__title--color-red':
                        option === ReferralStatusEnum.NO_SHOW || option === ReferralStatusEnum.CANCELLED,
                    'tracking-status__title--color-green':
                        option === ReferralStatusEnum.CONSULTATION_NOTES_PENDING ||
                        option === ReferralStatusEnum.PATIENT_VISIT_PENDING,
                }
            },
            handleStatusChange(selectedStatus) {
                const action = this.getApiActionForStatus(selectedStatus)
                if (!action) {
                    this.isProcessing = false
                    return
                }
                const { api, args } = action

                api(...args)
                    .then(() => {
                        this.isProcessing = false
                        this.showDropdown = false

                        const updatedReferral = {
                            ...this.referral,
                            status: selectedStatus,
                        }

                        this.emitEvent({
                            name: 'status-updated',
                            payload: updatedReferral,
                        })
                    })
                    .catch(() => {
                        this.isProcessing = false
                        this.showDropdown = true
                    })
            },
        },
    }
</script>
<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';
    .dropdown-arrow {
        display: inline-block;
        font-size: 12px;
        transition: transform 0.25s ease;
    }

    .dropdown-arrow.open {
        transform: rotate(180deg);
    }

    .status-dropdown {
        position: absolute;
        z-index: 20;
        background: #ffffff;
        border: 1px solid #e1e1e1;
        border-radius: 8px;
        padding: 6px 0;
        width: 160px;
        box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.08);
    }

    .status-dropdown__option {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 10px 14px 10px 40px;
        font-size: 14px;
        cursor: pointer;
        position: relative;
        transition: background 0.15s ease-in-out;
        border-radius: 4px;
    }

    .status-dropdown__option:hover {
        background: #f8f8f8;
    }

    .status-dropdown__option.tracking-status__title::before {
        content: '';
        position: absolute;
        left: 14px;
        top: 50%;
        transform: translateY(-50%);
        width: 14px;
        height: 14px;
        border-radius: 50%;
        background: currentColor;
    }
</style>
